import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";

const pageVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
};

const shakeVariants = {
  shake: {
    x: [0, -6, 6, -4, 4, -2, 0],
    transition: { duration: 0.4 },
  },
  idle: { x: 0 },
};

interface ValidationErrors {
  username?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
}

interface PasswordChecks {
  length: boolean;
  uppercase: boolean;
  lowercase: boolean;
  number: boolean;
}

function validatePassword(pw: string): PasswordChecks {
  return {
    length: pw.length >= 8,
    uppercase: /[A-Z]/.test(pw),
    lowercase: /[a-z]/.test(pw),
    number: /[0-9]/.test(pw),
  };
}

function validateForm(
  username: string,
  email: string,
  password: string,
  confirmPassword: string
): ValidationErrors {
  const errors: ValidationErrors = {};

  if (!username.trim()) {
    errors.username = "Username is required.";
  } else if (username.trim().length < 3) {
    errors.username = "Username must be at least 3 characters.";
  } else if (!/^[a-zA-Z0-9_-]+$/.test(username.trim())) {
    errors.username = "Only letters, numbers, underscores, and hyphens.";
  }

  if (!email.trim()) {
    errors.email = "Email is required.";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
    errors.email = "Please enter a valid email address.";
  }

  if (!password) {
    errors.password = "Password is required.";
  } else {
    const checks = validatePassword(password);
    if (!checks.length || !checks.uppercase || !checks.lowercase || !checks.number) {
      errors.password = "Password does not meet all requirements.";
    }
  }

  if (password !== confirmPassword) {
    errors.confirmPassword = "Passwords do not match.";
  }

  return errors;
}

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { addToast } = useToast();

  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [errors, setErrors] = useState<ValidationErrors>({});
  const [apiError, setApiError] = useState("");
  const [loading, setLoading] = useState(false);

  const passwordChecks = validatePassword(password);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError("");

    const validationErrors = validateForm(username, email, password, confirmPassword);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) return;

    setLoading(true);
    try {
      await register({
        username: username.trim(),
        email: email.trim(),
        displayName: displayName.trim() || undefined,
        password,
      });
      addToast({ type: "success", message: "Account created!" });
      navigate("/challenges");
    } catch (err: any) {
      setApiError(err?.message || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-ds-bg min-h-screen flex items-center justify-center relative overflow-hidden px-4">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full border border-ds-accent/[0.04] animate-pulse" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full border border-ds-accent/[0.06]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[200px] h-[200px] rounded-full border border-ds-accent/[0.08] animate-pulse" />
      </div>

      <motion.div
        variants={pageVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="w-full max-w-md"
      >
        <div className="ds-card p-8">
          <div className="mb-8">
            <h1 className="text-2xl font-semibold text-ds-text">Create account</h1>
            <div className="mt-3 h-px bg-gradient-to-r from-ds-accent to-transparent w-24" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm ds-text-secondary mb-1.5">Username</label>
              <input
                type="text"
                className="ds-input w-full"
                placeholder="pick_a_handle"
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value);
                  if (errors.username) setErrors((prev) => ({ ...prev, username: undefined }));
                }}
                required
                autoComplete="username"
              />
              {errors.username && (
                <p className="mt-1 text-xs text-red-400/80">{errors.username}</p>
              )}
            </div>

            <div>
              <label className="block text-sm ds-text-secondary mb-1.5">Email</label>
              <input
                type="email"
                className="ds-input w-full"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errors.email) setErrors((prev) => ({ ...prev, email: undefined }));
                }}
                required
                autoComplete="email"
              />
              {errors.email && (
                <p className="mt-1 text-xs text-red-400/80">{errors.email}</p>
              )}
            </div>

            <div>
              <label className="block text-sm ds-text-secondary mb-1.5">
                Display Name <span className="ds-text-muted">(optional)</span>
              </label>
              <input
                type="text"
                className="ds-input w-full"
                placeholder="How you'll appear"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                autoComplete="name"
              />
            </div>

            <div>
              <label className="block text-sm ds-text-secondary mb-1.5">Password</label>
              <input
                type="password"
                className="ds-input w-full"
                placeholder="Create a password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (errors.password) setErrors((prev) => ({ ...prev, password: undefined }));
                }}
                required
                autoComplete="new-password"
              />
              {errors.password && (
                <p className="mt-1 text-xs text-red-400/80">{errors.password}</p>
              )}
              <div className="mt-2 space-y-1">
                <Requirement met={passwordChecks.length} label="Min 8 characters" />
                <Requirement met={passwordChecks.uppercase} label="Uppercase letter" />
                <Requirement met={passwordChecks.lowercase} label="Lowercase letter" />
                <Requirement met={passwordChecks.number} label="Number" />
              </div>
            </div>

            <div>
              <label className="block text-sm ds-text-secondary mb-1.5">Confirm Password</label>
              <input
                type="password"
                className="ds-input w-full"
                placeholder="Re-enter your password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (errors.confirmPassword)
                    setErrors((prev) => ({ ...prev, confirmPassword: undefined }));
                }}
                required
                autoComplete="new-password"
              />
              {errors.confirmPassword && (
                <p className="mt-1 text-xs text-red-400/80">{errors.confirmPassword}</p>
              )}
            </div>

            {apiError && (
              <motion.div
                variants={shakeVariants}
                animate="shake"
                className="text-sm text-red-400 bg-red-400/10 border border-red-400/20 rounded-lg px-4 py-2.5"
              >
                {apiError}
              </motion.div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="ds-btn-primary w-full flex items-center justify-center gap-2"
            >
              {loading ? (
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
              ) : null}
              {loading ? "Creating account..." : "Create account"}
            </button>
          </form>

          <p className="mt-6 text-center text-sm ds-text-muted">
            Already have an account?{" "}
            <Link
              to="/login"
              className="text-ds-accent hover:text-ds-accent-light transition-colors"
            >
              Sign in
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
}

function Requirement({ met, label }: { met: boolean; label: string }) {
  return (
    <p
      className={`text-xs transition-colors duration-200 ${
        met ? "text-green-400" : "ds-text-muted"
      }`}
    >
      {met ? (
        <span className="mr-1">&#10003;</span>
      ) : (
        <span className="mr-1 opacity-40">&mdash;</span>
      )}
      {label}
    </p>
  );
}
